import * as yup from "yup";
import { useFormik } from "formik";
import {
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import AutoCompleteAddress from "../../../components/AutoCompleteAddress";
import PhoneInput from "react-phone-input-2";
import dateIcon from "../../../images/date.png";
import bagsIcon from "../../../images/suitcase.png";
import adultsIcon from "../../../images/couple.png";
import childsIcon from "../../../images/children.png";
import { TerminalGateTo } from "../../../utils/TerminalGate";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  VehicleBags,
  VehicleSeat,
  VehicleSurcharge,
} from "../../../utils/VehicleDetail";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AirplaneTicketIcon from "../../../images/tiketIcon.png";
import TimeIcon from "../../../images/timeIcon.png";
import setHours from "date-fns/setHours";
import setMinutes from "date-fns/setMinutes";
import dayjs from "dayjs";
import {
  checkExtraCharge,
  GetSurchargePrice,
} from "../../../components/CheckSurcharge";
import InfoIcon from "@mui/icons-material/Info";
import { GetListTimeExcluded } from "src/utils/CheckTimeExcluded";
import { formatTime, generateNewTime } from "src/utils/tools";

interface FormikToAirportProps {
  vehicleId: number;
  editValue: any;
  editState: boolean;
}

const FormikToAirport = (props: FormikToAirportProps) => {
  const { vehicleId, editValue, editState, ...rest } = props;
  const [seater, setSeater] = useState<number>(0);
  const [bagValue, setBagValue] = useState<number>(0);
  const [addressOptions, setAddressOptions] = useState([]);
  const [isIncludeSurcharge, setIsIncludeSurcharge] = useState(false);
  const [surchargePrice, setSurchargePrice] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isSurchargeActive, setIsSurchargeActive] = useState(false);
  const [listTimeExclude, setListTimeExclude] = useState<string[]>([]);
  const [isTimeNotAvailable, setIsTimeNotAvailable] = useState(false);
  const DateValidation = new Date(Date.now() + 3600 * 1000 * 24);

  let navigate = useNavigate();

  const validationSchema = yup.object({
    name: yup.string().required("Name is Required"),
    email: yup.string().email().required("Email is Required"),
    from: yup.string().required("Please Select Pick Up Destinations Address"),
    to: yup.string().required("Please Select Destinations Airport"),
    descriptions: yup.string().required("Descriptions is Required"),
    date: yup
      .date()
      .test("Date_Validation", "Date Not Valid", function () {
        return !isTimeNotAvailable;
      })
      .test("Date_Validation", "Date Not Valid", function (value) {
        if (!value) return false;

        const minDate = new Date();
        minDate.setHours(minDate.getHours() + 24);

        return value > minDate;
      })
      .required("Please Input Date and Time"),
    adults: yup.number().required().min(1, "Please Input Adults Amount"),
    childs: yup.number().typeError("Please Input 0 When No Child"),
    bags: yup.number().required().typeError("Please Input Bags Amount"),
    contactnumber: yup.string().min(8, "Must be exactly 8 digits").required(),
    flightnumber: yup.string(),
    isFromFound: yup.boolean().required().oneOf([true], "Address Not Found"),
  });

  function editInitialValue() {
    if (editValue !== null) {
      formik.setFieldValue("from", editValue.from);
      formik.setFieldValue("email", editValue.email);
      formik.setFieldValue("name", editValue.name);
      formik.setFieldValue("to", editValue.to);
      formik.setFieldValue("date", new Date(editValue.date));
      formik.setFieldValue("descriptions", editValue.descriptions);
      formik.setFieldValue("adults", editValue.adults);
      formik.setFieldValue("childs", editValue.childs);
      formik.setFieldValue("bags", editValue.bags);
      formik.setFieldValue("contactnumber", editValue.contactnumber);
      formik.setFieldValue("flightnumber", editValue.flightnumber);
    }
  }

  const formik = useFormik({
    initialValues: {
      isFromAirport: false,
      vehicleId: vehicleId,
      email: "",
      name: "",
      from: "",
      to: "",
      date: generateNewTime(listTimeExclude),
      descriptions: "",
      adults: "",
      childs: "",
      bags: 0,
      contactnumber: "",
      flightnumber: "",
      isFromFound: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      localStorage.setItem("form-value", JSON.stringify(values));
      navigate("/BookingSummary", { replace: true });
    },
  });

  const FetchVehicleData = async () => {
    setSeater(await VehicleSeat(vehicleId));
    setBagValue(await VehicleBags(vehicleId));
    setIsSurchargeActive(await VehicleSurcharge(vehicleId));
  };

  useEffect(() => {
    editInitialValue();
    FetchVehicleData();
    formik.setFieldValue("isFromFound", true);
  }, [editValue]);

  useEffect(() => {
    if (addressOptions.length === 0) {
      formik.setFieldValue("isFromFound", false);
    } else {
      formik.setFieldValue("isFromFound", true);
    }
  }, [addressOptions]);

  const handleSurcharge = async () => {
    setIsIncludeSurcharge(await checkExtraCharge(formik.values.date));
    setSurchargePrice(await GetSurchargePrice());
  };

  useEffect(() => {
    handleSurcharge();
  }, [formik.values.date]);

  // Scroll to top when field error / empty
  useEffect(() => {
    const el = document.querySelector(".Mui-error, [data-error]");
    const field = document.getElementById("datePickup");
    (el?.parentElement && field)?.scrollIntoView({
      behavior: "auto",
      block: "center",
      inline: "center",
    });
  }, [formik.isSubmitting]);

  const handleShowTooltip = () => {
    showTooltip ? setShowTooltip(false) : setShowTooltip(true);
  };

  const CheckBookingDate = async (isFirstLoad: boolean) => {
    let listTimeExclude = await GetListTimeExcluded(
      vehicleId,
      "ToAirport",
      formik.values.date
    );
    if (listTimeExclude !== null) {
      setListTimeExclude(listTimeExclude.excludedTimes);
      setIsTimeNotAvailable(listTimeExclude.isDateExclude);
    }
  };

  useEffect(() => {
    editValue === null && CheckBookingDate(true);
  }, []);

  useEffect(() => {
    CheckBookingDate(false);
  }, [formik.values.date]);

  const CustomTimeClassname = (time: any) => {
    let className = "flex";
    if (listTimeExclude.includes(formatTime(time))) {
      className =
        "pointer-events-none after:content-['__Fully_Booked'] opacity-50 text-red-600 flex gap-1";
    }
    return className;
  };

  return (
    <form onSubmit={formik.handleSubmit} id="formikForm">
      <div className="flex flex-wrap xl:space-x-14 space-x-0 md:jusitfy-between justify-center">
        <div className="flex flex-col max-w-[470px] w-[100%] xl:w-auto">
          <div className="formTextField">
            <a>From*</a>
            <AutoCompleteAddress
              id="from"
              name="from"
              placeholder="Enter Pick Up"
              setInput={(text) => {
                formik.setFieldValue("from", text);
              }}
              addressOptions={addressOptions}
              setAddressOptions={setAddressOptions}
              input={formik.values.from}
              error={
                (formik.touched.from && Boolean(formik.errors.from)) ||
                (!formik.values.isFromFound && formik.values.from)
              }
              helperText={
                formik.touched.isFromFound && formik.errors.isFromFound
              }
            />
          </div>
          <div className="formTextField">
            <a>Descriptions*</a>
            <TextField
              id="descriptions"
              name="descriptions"
              placeholder="Ex. Apartment A"
              size="small"
              value={formik.values.descriptions}
              onChange={formik.handleChange}
              error={
                formik.touched.descriptions &&
                Boolean(formik.errors.descriptions)
              }
            />
          </div>
          <div className="formTextField max-h-[64px]">
            <a>To*</a>
            <Select
              displayEmpty
              id="to"
              name="to"
              value={formik.values.to ?? ""}
              onChange={formik.handleChange}
              size="small"
              error={formik.touched.to && Boolean(formik.errors.to)}
            >
              <MenuItem disabled value="">
                <p className="text-[#a9a9a9]">Select Airport Drop Off</p>
              </MenuItem>
              {TerminalGateTo.map((item) => (
                <MenuItem style={{ whiteSpace: "normal" }} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className="formTextField">
            <a>Contact No*</a>
            <PhoneInput
              country={"sg"}
              enableSearch
              autoFormat={false}
              value={formik.values.contactnumber ?? ""}
              onChange={(a, x, e) => formik.handleChange(e)}
              inputStyle={{
                width: "100%",
                height: "40px",
                borderColor: `${
                  formik.touched.contactnumber &&
                  Boolean(formik.errors.contactnumber)
                    ? "red"
                    : ""
                }`,
              }}
              dropdownStyle={{ padding: "0px 10px" }}
              inputProps={{
                id: "contactnumber",
                name: "contactnumber",
                required: true,
              }}
            />
          </div>
          <div className="flex space-x-10 md:space-x-7">
            <div className="formTextField">
              <div className="flex gap-2 items-center">
                <a>Date Pickup*</a>
                {isTimeNotAvailable && (
                  <div className="text-red-500 text-[12px]">Full Book</div>
                )}
              </div>
              <div className="formTextField row">
                <div className="flex flex-col">
                  <div className="flex iconField">
                    <img src={dateIcon} style={{ marginRight: "10px" }}></img>
                    <DatePicker
                      id="datePickup"
                      name="datePickup"
                      selected={formik.values.date}
                      onChange={(date) => {
                        date?.setSeconds(0);
                        formik.setFieldValue("date", date);
                      }}
                      minDate={new Date(Date.now())}
                      onKeyDown={(event) => {
                        event.preventDefault();
                      }}
                      dateFormat="MMMM d, yyyy"
                      className={`flex border ${
                        isTimeNotAvailable ||
                        dayjs(formik.values.date).isBefore(DateValidation)
                          ? "border-red-500"
                          : "border-gray-300"
                      } hover:border-black py-[7px] pl-[10px] rounded`}
                    />
                  </div>
                  {dayjs(formik.values.date).isBefore(DateValidation) ? (
                    <div className="text-red-600">The date is fully booked</div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
            <div className="formTextField">
              <div className="flex space-x-1 items-center">
                <div>Time Pickup*</div>
                {isIncludeSurcharge && isSurchargeActive && (
                  <Tooltip
                    title={`Midnight surcharge applies for $${(
                      surchargePrice / 100
                    ).toFixed(2)}`}
                    arrow
                    open={showTooltip}
                    onClick={handleShowTooltip}
                  >
                    <InfoIcon sx={{ fontSize: 15, color: "gray" }} />
                  </Tooltip>
                )}
              </div>

              <div className="formTextField row">
                <div className="iconField">
                  <img
                    src={TimeIcon}
                    style={{ marginRight: "10px", width: "25px" }}
                  ></img>
                  <DatePicker
                    id="timePickup"
                    name="timePickup"
                    selected={formik.values.date}
                    onChange={(date) => {
                      date?.setSeconds(0);
                      formik.setFieldValue("date", date);
                    }}
                    minDate={new Date(Date.now() + 3600 * 1000)}
                    minTime={
                      dayjs(formik.values.date).isAfter(
                        dayjs(new Date(Date.now() + 3600 * 1000)),
                        "day"
                      )
                        ? isSurchargeActive
                          ? setHours(setMinutes(new Date(), 0), 24)
                          : setHours(setMinutes(new Date(), 0), 7)
                        : new Date(Date.now() + 3600 * 1000)
                    }
                    maxTime={
                      isSurchargeActive
                        ? setHours(setMinutes(new Date(), 30), 23)
                        : setHours(setMinutes(new Date(), 0), 23)
                    }
                    showTimeSelect
                    showTimeSelectOnly
                    onKeyDown={(event) => {
                      event.preventDefault();
                    }}
                    // excludeTimes={excludeTimes}
                    timeFormat="HH:mm"
                    dateFormat="HH:mm"
                    className={`flex border w-full ${
                      isTimeNotAvailable ||
                      dayjs(formik.values.date).isBefore(DateValidation)
                        ? "border-red-500"
                        : "border-gray-300"
                    } hover:border-black py-[7px] pl-[10px] rounded`}
                    timeClassName={CustomTimeClassname}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col max-w-[475px]">
          <div className="formTextField">
            <a>Name*</a>
            <TextField
              id="name"
              name="name"
              size="small"
              placeholder="Name"
              value={formik.values.name}
              onChange={(event) => {
                formik.setFieldValue("name", event.target.value);
              }}
              error={formik.touched.name && Boolean(formik.errors.name)}
            />
          </div>
          <div className="formTextField">
            <a>Email*</a>
            <TextField
              id="email"
              size="small"
              name="email"
              placeholder="Email"
              value={formik.values.email}
              onChange={(event) => {
                formik.setFieldValue("email", event.target.value);
              }}
              error={formik.touched.email && Boolean(formik.errors.email)}
            />
          </div>
          <div className="flex space-x-10 md:space-x-5">
            <div className="formTextField">
              <a>Flight Number</a>
              <TextField
                id="flightnumber"
                name="flightnumber"
                placeholder="Flight Number"
                value={formik.values.flightnumber}
                size="small"
                onChange={formik.handleChange}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <img
                        src={AirplaneTicketIcon}
                        style={{ marginRight: "10px", width: "25px" }}
                      ></img>
                    </InputAdornment>
                  ),
                }}
                error={
                  formik.touched.flightnumber &&
                  Boolean(formik.errors.flightnumber)
                }
              />
            </div>
            <div className="formTextField">
              <div className="flex space-x-1 items-center">
                <div>Bags*</div>
                <div className="text-gray-400 text-sm">
                  (Max {bagValue} Bags)
                </div>
              </div>

              <div className="formTextField row">
                <div className="iconField">
                  <TextField
                    id="bags"
                    name="bags"
                    size="small"
                    placeholder="No. Of Bags"
                    type="number"
                    value={formik.values.bags}
                    style={{ width: "100%" }}
                    InputProps={{
                      inputProps: {
                        min: 0,
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <img
                            src={bagsIcon}
                            style={{ marginRight: "10px" }}
                          ></img>
                        </InputAdornment>
                      ),
                    }}
                    onChange={(event) => {
                      var value = parseInt(event.target.value, 10);

                      if (value > bagValue) value = bagValue;

                      formik.setFieldValue("bags", value);
                    }}
                    error={formik.touched.bags && Boolean(formik.errors.bags)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="formTextField">
            <a>Guest*</a>
            <div className="flex justify-between md:space-x-5 md:space-y-0 space-y-0 space-x-5">
              <div className="iconField max-w-[374px]">
                <TextField
                  id="adults"
                  name="adults"
                  placeholder="No. Of Adults"
                  type="number"
                  size="small"
                  style={{ width: "100%" }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <img
                          src={adultsIcon}
                          className="w-[25px]"
                          style={{ marginRight: "10px" }}
                        ></img>
                      </InputAdornment>
                    ),
                  }}
                  value={formik.values.adults}
                  onChange={(event) => {
                    var value = parseInt(event.target.value, 10);
                    var formikValueChilds = parseInt(formik.values.childs, 10);
                    if (value + formikValueChilds > seater || value > seater)
                      value = seater - formikValueChilds;
                    formik.setFieldValue("adults", value);
                  }}
                  error={formik.touched.adults && Boolean(formik.errors.adults)}
                />
              </div>
              <div className="iconField max-w-[374px]">
                <TextField
                  id="childs"
                  name="childs"
                  size="small"
                  placeholder="No. Of Childs"
                  type="number"
                  style={{ width: "100%" }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <img
                          src={childsIcon}
                          className="w-[25px]"
                          style={{ marginRight: "10px" }}
                        ></img>
                      </InputAdornment>
                    ),
                  }}
                  value={formik.values.childs}
                  onChange={(event) => {
                    var value = parseInt(event.target.value, 10);
                    var formikValueAdults = parseInt(formik.values.adults, 10);
                    if (value + formikValueAdults > seater || value > seater)
                      value = seater - formikValueAdults;
                    formik.setFieldValue("childs", value);
                  }}
                  error={formik.touched.childs && Boolean(formik.errors.childs)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default FormikToAirport;
